import * as React from "react";
import { toast } from "sonner";
import { useMessageActions } from "../hooks/useMessageActions";
import type { ChatMessage, ISubmitFeedbackRequest } from "../../types/discuss.types";
import type { ResearchChatMessage } from "../../types/research.types";
import type { useSubmitFeedback } from "../../hooks/useSubmitFeedback";

const RatingDialog = React.lazy(() => import("./RatingDialog"));
import CreateRequestDialog from "./request-gallery/CreateRequestDialog";

type Message = ChatMessage | ResearchChatMessage;

export interface RatingSubmitDeps {
  documentId?: string;
  documentTitle?: string;
  source: "discuss" | "research";
  message: Message;
  prevMessage?: Message;
  submitFeedback: ReturnType<typeof useSubmitFeedback>["mutateAsync"];
  setIsSubmitting: (v: boolean) => void;
  setShowRatingDialog: (v: boolean) => void;
}

export async function handleRatingSubmitAction(
  rating: number,
  comment: string,
  deps: RatingSubmitDeps
): Promise<void> {
  deps.setIsSubmitting(true);
  try {
    const docId = deps.documentId ?? "";
    const docTitle = deps.documentTitle ?? "";
    const feedbackPayload = {
      document_id: docId !== "" ? [docId] : [],
      source: (deps.source === "discuss"
        ? "Discuss"
        : "ResearchDraft") as ISubmitFeedbackRequest["source"],
      prompt: null,
      query: deps.message.question,
      response: deps.message.response,
      rating: rating as 1 | 2 | 3 | 4 | 5,
      comment: comment,
      current_document_id: docId,
      document_title: docTitle !== "" ? [docTitle] : [],
    };
    const response = await deps.submitFeedback(feedbackPayload);
    if (!(deps.source === "discuss" && rating <= 2 && response?.refinedResponse != null)) {
      toast.success("Feedback Submitted", { description: "Thank you for your feedback" });
    }
    deps.setShowRatingDialog(false);
  } catch (_error) {
    toast.error("Submission Failed", {
      description: "Failed to submit feedback. Please try again.",
    });
  } finally {
    deps.setIsSubmitting(false);
  }
}

export function useMessageActionsWithLoading(documentId?: string, documentTitle?: string) {
  const [isCopying, setIsCopying] = React.useState(false);
  const [isInserting, setIsInserting] = React.useState(false);
  const [isCreatingDoc, setIsCreatingDoc] = React.useState(false);
  const {
    handleCopyMessage: baseCopy,
    handleInsertMessageToDocument: baseInsert,
    handleCreateDocumentFromMessage: baseCreateDoc,
  } = useMessageActions({ documentId, documentTitle });

  const handleCopyMessage = async (text: string) => {
    setIsCopying(true);
    try {
      await baseCopy(text);
    } finally {
      setIsCopying(false);
    }
  };
  const handleInsertMessageToDocument = async (text: string) => {
    setIsInserting(true);
    try {
      await baseInsert(text);
    } finally {
      setIsInserting(false);
    }
  };
  const handleCreateDocumentFromMessage = async (text: string, title: string) => {
    setIsCreatingDoc(true);
    try {
      await baseCreateDoc(text, title);
    } finally {
      setIsCreatingDoc(false);
    }
  };

  return {
    handleCopyMessage,
    handleInsertMessageToDocument,
    handleCreateDocumentFromMessage,
    isCopying,
    isInserting,
    isCreatingDoc,
  };
}

export const MessageDialogs: React.FC<{
  showRatingDialog: boolean;
  setShowRatingDialog: (v: boolean) => void;
  isSubmitting: boolean;
  ratingDeps: RatingSubmitDeps;
  isCreateRequestOpen: boolean;
  setIsCreateRequestOpen: (v: boolean) => void;
  createRequestData?: { name: string; content: string };
  source: "discuss" | "research";
}> = ({
  showRatingDialog,
  setShowRatingDialog,
  isSubmitting,
  ratingDeps,
  isCreateRequestOpen,
  setIsCreateRequestOpen,
  createRequestData,
  source,
}) => (
  <>
    {showRatingDialog && (
      <React.Suspense fallback={null}>
        <RatingDialog
          isOpen={showRatingDialog}
          onOpenChange={setShowRatingDialog}
          onSubmit={(rating: number, comment: string) =>
            handleRatingSubmitAction(rating, comment, ratingDeps)
          }
          isSubmitting={isSubmitting}
        />
      </React.Suspense>
    )}
    <CreateRequestDialog
      open={isCreateRequestOpen}
      onOpenChange={setIsCreateRequestOpen}
      initialData={createRequestData}
      type={source}
    />
  </>
);
